/**
 * Quellen-agnostischer Export der MNO-IP-Ranges (Issue #324).
 *
 * Reines, DOM-freies Modul: flacht die Eintraege auf einen Record pro CIDR ab
 * und serialisiert nach CSV / JSON / XML. Der Browser-Download-Glue liegt
 * getrennt in `./trigger-download`. Greift spaeter unveraendert auf die echte
 * Facade-Operator-/Range-Quelle (gleiche Typen).
 */
import type {
  MnoIpRangeEntry,
  MnoIpRangeStatus,
  MnoIpVersion,
  MnoOperator,
} from '@/types/mno-ip-range';

export type ExportFormat = 'csv' | 'json' | 'xml';

/** Ein flacher Export-Record — genau ein CIDR-Block. */
export interface IpRangeExportRow {
  /** ISO-3166-1 alpha-2. */
  country: string;
  operatorId: string;
  operatorName: string;
  cidr: string;
  ipVersion: MnoIpVersion;
  status: MnoIpRangeStatus;
}

/** Feld- und Spaltenreihenfolge fuer CSV-Header und XML-Kindelemente. */
const FIELDS: (keyof IpRangeExportRow)[] = [
  'country',
  'operatorId',
  'operatorName',
  'cidr',
  'ipVersion',
  'status',
];

/**
 * Flacht die Eintraege auf einen Record pro CIDR ab. `operatorName` wird aus der
 * Operator-Quelle aufgeloest und faellt auf `operatorId` zurueck, wenn der
 * Operator dort fehlt. `status` ohne Wert gilt als `active` — wie in der Liste.
 * Alle Eintraege werden exportiert, auch inaktive (die Spalte erlaubt das
 * Filtern beim Konsumenten).
 */
export function flattenEntries(
  entries: MnoIpRangeEntry[],
  operators: MnoOperator[]
): IpRangeExportRow[] {
  const nameById = new Map(operators.map((op) => [op.operatorId, op.name]));
  const rows: IpRangeExportRow[] = [];
  for (const entry of entries) {
    const operatorName = nameById.get(entry.operatorId) ?? entry.operatorId;
    const status: MnoIpRangeStatus = entry.status ?? 'active';
    for (const range of entry.ipRanges) {
      rows.push({
        country: entry.country.code,
        operatorId: entry.operatorId,
        operatorName,
        cidr: range.cidr,
        ipVersion: range.ipVersion,
        status,
      });
    }
  }
  return rows;
}

// --- CSV (RFC 4180) ---

/** Quotet ein Feld, wenn es Komma, Anfuehrungszeichen oder Zeilenumbruch enthaelt. */
function csvField(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function toCsv(rows: IpRangeExportRow[]): string {
  const header = FIELDS.join(',');
  const lines = rows.map((row) =>
    FIELDS.map((field) => csvField(String(row[field]))).join(',')
  );
  return [header, ...lines].join('\r\n');
}

// --- JSON ---

export function toJson(rows: IpRangeExportRow[]): string {
  return JSON.stringify(rows, null, 2);
}

// --- XML ---

/** XML-Escaping fuer Text-Inhalt: & < > " ' (& zuerst). */
function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function toXml(rows: IpRangeExportRow[]): string {
  const body = rows
    .map((row) => {
      const children = FIELDS.map(
        (field) => `    <${field}>${xmlEscape(String(row[field]))}</${field}>`
      ).join('\n');
      return `  <range>\n${children}\n  </range>`;
    })
    .join('\n');
  const inner = body ? `\n${body}\n` : '\n';
  return `<?xml version="1.0" encoding="UTF-8"?>\n<mnoIpRanges>${inner}</mnoIpRanges>`;
}

// --- Format-Bridge (Dateiname + MIME + Inhalt) ---

const MIME: Record<ExportFormat, string> = {
  csv: 'text/csv;charset=utf-8',
  json: 'application/json;charset=utf-8',
  xml: 'application/xml;charset=utf-8',
};

const SERIALIZERS: Record<ExportFormat, (rows: IpRangeExportRow[]) => string> = {
  csv: toCsv,
  json: toJson,
  xml: toXml,
};

function dateStamp(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface IpRangeExportFile {
  filename: string;
  mime: string;
  content: string;
}

/**
 * Baut Dateiname, MIME-Typ und serialisierten Inhalt fuer einen Export.
 * `date` kommt im Client aus `new Date()`; als Parameter bleibt das Modul
 * testbar und DOM-frei.
 */
export function buildIpRangeExport(
  format: ExportFormat,
  entries: MnoIpRangeEntry[],
  operators: MnoOperator[],
  date: Date
): IpRangeExportFile {
  const rows = flattenEntries(entries, operators);
  return {
    filename: `mno-ip-ranges-${dateStamp(date)}.${format}`,
    mime: MIME[format],
    content: SERIALIZERS[format](rows),
  };
}
